import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

import { shouldReportBoardError } from "../boardFailures";

import { boardQueryKeys } from "./boardQueryKeys";
import { refreshBoardAndInvalidateLists } from "./refreshBoardQueries";

/**
 * Delete a board. Once the platform confirms the delete, the board's cached
 * record is dropped and the org's board lists invalidated. When the delete
 * fails, the board's record is refetched as well, since a delete whose
 * response was lost may still have happened.
 */
export function useDeleteBoard() {
  const { boardService } = useDomainServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      boardId,
      orgId,
    }: {
      boardId: string;
      orgId: string;
    }) => {
      try {
        await boardService.deleteBoard(boardId, orgId);
      } catch (error) {
        await refreshBoardAndInvalidateLists(
          queryClient,
          boardService,
          boardId,
          orgId,
        );
        throw error;
      }
      await queryClient.cancelQueries({
        queryKey: boardQueryKeys.id(boardId, orgId),
        exact: true,
      });
      queryClient.removeQueries({
        queryKey: boardQueryKeys.id(boardId, orgId),
        exact: true,
      });
      await queryClient.cancelQueries({ queryKey: boardQueryKeys.list(orgId) });
      await queryClient.invalidateQueries({
        queryKey: boardQueryKeys.list(orgId),
      });
    },
    onError: (error) => {
      if (shouldReportBoardError(error)) {
        ErrorMonitoringService.captureError(error);
      }
    },
  });
}
